import { ChangeEvent, useEffect, useState } from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import type { Suggestions } from '@mint-sdk/api-recommendations';
import { useSetAtom } from 'jotai';

// TODO: Replace with tw-pattern when available
import {
  capitalize,
  FilterButton,
  FilterSearchContainer,
  FilterSearchInputBase,
  PopoverItem,
} from '@myn/mui';

import { useSuggestionFilter } from '../../../hooks/useSuggestionFilter.js';
import { selectedSuggestionAtom } from '../../../store/index.js';
import {
  SuggestionAction,
  SuggestionFilterActions,
} from '../../../store/suggestion/suggestionFilter.js';
import type { SelectedFilterState } from '../../../types/suggestion.js';
import ChipPopover from '../../molecules/ChipPopover/ChipPopover.jsx';
import {
  selectSuggestionFilters,
  selectSuggestionSearchValue,
} from './helpers.js';

type PopperContent = {
  text: keyof Suggestions.AvailableFiltersType;
};

const SuggestionFilter = ({ isSkeleton }: { isSkeleton?: boolean }) => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const { selectedSuggestionFilter, setSuggestionFilter } =
    useSuggestionFilter();

  const setSelectedSuggestion = useSetAtom(selectedSuggestionAtom);

  const [filtersOpen, setFiltersOpen] = useState<
    Record<keyof Suggestions.AvailableFiltersType, boolean>
  >({
    campaigns: false,
    channels: false,
    platforms: false,
  });

  const setFiltersPopoverOpen = (filterName: string, value: boolean) => {
    setFiltersOpen((prev) => ({ ...prev, [filterName]: value }));
  };

  const [searchValue, setSearchValue] = useState<string>(
    selectSuggestionSearchValue(selectedSuggestionFilter),
  );

  const debouncedSearchValue = useDebounce(searchValue, 500);

  const [selectedFilter, setSelectedFilter] = useState<SelectedFilterState[]>(
    selectSuggestionFilters(selectedSuggestionFilter),
  );

  useEffect(() => {
    setSuggestionFilter({
      type: SuggestionFilterActions.SET_CAMPAIGN_NAME,
      data: debouncedSearchValue,
    });
    setSelectedSuggestion(null);
  }, [debouncedSearchValue]);

  useEffect(() => {
    // TODO: We should re-think this approach in future
    const timeout = setTimeout(() => {
      setSelectedFilter(selectSuggestionFilters(selectedSuggestionFilter));
      setSearchValue(selectSuggestionSearchValue(selectedSuggestionFilter));
    }, 0);
    return () => clearTimeout(timeout);
  }, [selectedSuggestionFilter]);

  const [filterButtonAnchorEl, setFilterButtonAnchorEl] =
    useState<Element | null>(null);

  const popperContent: PopperContent[] = selectedFilter.map((filter) => {
    return {
      text: Object.keys(filter)[0] as keyof Suggestions.AvailableFiltersType,
    };
  });

  const handleAnchorElChange = (newAnchorEl: Element | null) => {
    setFilterButtonAnchorEl(newAnchorEl);
  };

  const handleFilterButtonClick = (event: React.MouseEvent<Element> | null) => {
    setFilterButtonAnchorEl(event?.currentTarget as Element);
  };

  const handleClose = () => {
    setFilterButtonAnchorEl(null);
  };
  const handleFilterItem = (item: PopperContent) => {
    setSelectedFilter((prevState) => {
      return prevState.map((filter) => {
        // Check if this object has the key we're looking for
        if (item.text in filter) {
          // Return a new object with the updated value
          setFiltersPopoverOpen(item.text, true);
          return { [item.text]: filter[item.text] ?? [] };
        }
        // Return unchanged object if it doesn't have the key
        return filter;
      });
    });

    handleClose();
  };

  const handleFilterApply = (data: SuggestionAction) => {
    setSuggestionFilter(data);
    setFilterButtonAnchorEl(null);
    setSelectedSuggestion(null);
  };

  const handleFilterSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleFilterSearchClear = () => {
    setSearchValue('');
  };

  const isEditingFilter =
    (filtersOpen.campaigns && selectedSuggestionFilter.campaigns.length) ||
    (filtersOpen.channels && selectedSuggestionFilter.channels.length) ||
    (filtersOpen.platforms && selectedSuggestionFilter.platforms.length);

  return (
    <FilterSearchContainer
      disabled={isSkeleton}
      sx={{
        height: 40,
        flex: '1 1 91%',
        width: '100%',
        padding: (theme) => theme.spacing(1, 0),
        marginTop: (theme) => theme.spacing(2),
      }}
    >
      <div className="ml-11 flex flex-row gap-3">
        {selectedFilter.map((filter, index) => {
          // if null we don't render  chip, if it`s empty array we render and show select with options
          return Object.values(filter)[0] ? (
            <ChipPopover
              filtersOpen={filtersOpen}
              setFiltersOpen={setFiltersPopoverOpen}
              key={`${index}-${filter[0]}`}
              filter={filter}
              setSelectedFilter={setSelectedFilter}
              handleFilterApply={handleFilterApply}
            />
          ) : null;
        })}
      </div>
      <FilterSearchInputBase
        placeholder={t('Search and filter')}
        onChange={handleFilterSearch}
        value={searchValue}
        onClear={handleFilterSearchClear}
        tooltipProps={{
          open: false,
        }}
        disabled={isSkeleton}
      />
      <FilterButton
        aria-label="open filter popover"
        disabled={isSkeleton}
        anchorEl={filterButtonAnchorEl}
        onClick={isEditingFilter ? undefined : handleFilterButtonClick}
        onClose={handleClose}
        onAnchorElChange={handleAnchorElChange}
        popoverContent={popperContent.map((item, key) => (
          <PopoverItem
            key={`${key}-${item.text}`}
            text={capitalize(item.text)}
            onClick={() => handleFilterItem(item)}
          />
        ))}
        popoverTitle={t('FILTER BY')}
      />
    </FilterSearchContainer>
  );
};

export default SuggestionFilter;
